import { ChangeEventHandler, FC } from 'react';

import { Box, BoxProps, FormLabel, InputBase, Typography, styled } from '@mui/material';
import { useField } from 'formik';

export type OptionTypes = {
    value: string;
    label: string;
};

type SearchInputProps = {
    name: string;
    value: string;
    onChange: ChangeEventHandler<HTMLInputElement>;
    label: string;
    boxStyling?: BoxProps;
    onSearch?: (value?: string, name?: string) => void;
    error?: string;
    searchText?: string;
    placeholder?: string;
    maskedValue?: string;
    onClearField?: (value: boolean) => void;
    dataTestId?: string;
    disabled?: boolean;
};

const InputComponent = styled(InputBase)({
    width: '100px',
    '&:focus': {
        backgroundColor: 'transparent',
        outline: 'none',
    },
});

export const SearchInput: FC<SearchInputProps> = ({
    name,
    value,
    onChange,
    label,
    boxStyling,
    error,
    onSearch,
    searchText,
    placeholder,
    maskedValue,
    onClearField,
    dataTestId = `${name}-input`,
    disabled,
}) => {
    const [{ onBlur }, { touched }, helpers] = useField(name);
    const borderColor = error && touched ? 'red' : '#F39E4E';

    const displayValue = maskedValue || value;

    const handleOnclearField = () => {
        onClearField(true);
    };

    return (
        <Box display="flex" flexDirection="column" flex={0.49} {...boxStyling}>
            <FormLabel sx={{ marginBottom: '8px', color: '#000', fontWeight: 600 }}>
                {label}
            </FormLabel>

            <Box
                display="flex"
                flexDirection="row"
                border={`1px solid ${borderColor}`}
                height="51px"
                borderRadius="8px"
                overflow="hidden"
            >
                {maskedValue ? (
                    <InputComponent
                        inputProps={{
                            'data-testid': dataTestId,
                        }}
                        onBlur={onBlur}
                        placeholder={placeholder ?? label}
                        value={displayValue}
                        onChange={onChange}
                        name={name}
                        sx={{ flex: 1, borderWidth: 0, margin: 'auto 14px' }}
                        autoComplete="off"
                        onClick={handleOnclearField}
                        disabled={disabled}
                    />
                ) : (
                    <InputComponent
                        inputProps={{
                            'data-testid': dataTestId,
                        }}
                        onBlur={onBlur}
                        placeholder={placeholder ?? label}
                        value={displayValue}
                        onChange={onChange}
                        name={name}
                        sx={{ flex: 1, borderWidth: 0, margin: 'auto 14px' }}
                        autoComplete="off"
                        disabled={disabled}
                    />
                )}
                <Box
                    bgcolor={borderColor}
                    textAlign="center"
                    paddingY="15px"
                    paddingX="25px"
                    onClick={() => onSearch(value, name)}
                    sx={{ cursor: 'pointer' }}
                >
                    <Typography fontSize="16px" color="white">
                        {searchText || 'Search'}
                    </Typography>
                </Box>
            </Box>
            {error && touched && <label className="error">{error}</label>}
        </Box>
    );
};
